<template>
  <div class="standard-select">
    <div class="step-badge">
      <i class="el-icon-s-order"></i>
      <span>{{ $t('buy.steps.badge.step3') }}</span>
    </div>
    <div class="section-header">
      <h3>{{ $t('buy.standards.title') }}</h3>
      <p class="section-desc">{{ $t('buy.standards.subtitle') }}</p>
    </div>

    <!-- 分类筛选 -->
    <div class="category-filter">
      <el-radio-group v-model="activeCategory" size="small">
        <el-radio-button label="all">{{ $t('buy.standards.categories.all') }}</el-radio-button>
        <el-radio-button label="quality">{{ $t('buy.standards.categories.quality') }}</el-radio-button>
        <el-radio-button label="energy">{{ $t('buy.standards.categories.energy') }}</el-radio-button>
        <el-radio-button label="product">{{ $t('buy.standards.categories.product') }}</el-radio-button>
        <el-radio-button label="environment">{{ $t('buy.standards.categories.environment') }}</el-radio-button>
        <el-radio-button label="process">{{ $t('buy.standards.categories.process') }}</el-radio-button>
        <el-radio-button label="security">{{ $t('buy.standards.categories.security') }}</el-radio-button>
        <el-radio-button label="medical">{{ $t('buy.standards.categories.medical') }}</el-radio-button>
        <el-radio-button label="healthSafety">{{ $t('buy.standards.categories.healthSafety') }}</el-radio-button>
        <el-radio-button label="creator">{{ $t('buy.standards.categories.creator') }}</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 已选统计 -->
    <div class="selection-summary">
      <div class="summary-left">
        <i class="el-icon-document-checked"></i>
        <span>{{ $t('buy.standards.selectedCount', { count: selectedStandards.length }) }}</span>
      </div>
      <div class="summary-right">
        <span class="total-label">{{ $t('buy.standards.moduleCost') }}:</span>
        <span class="total-amount">{{ currency }}  {{ formatPrice(moduleTotalPrice) }}</span>
        <span class="total-period">/ {{ $t('buy.standards.perYear') }}</span>
      </div>
    </div>

    <!-- 标准卡片网格 -->
    <div class="standards-grid">
      <div 
        v-for="standard in filteredStandards"
        :key="standard.id"
        class="standard-card"
        :class="{ 
          selected: isSelected(standard.id),
          included: standard.included 
        }"
        @click="toggleStandard(standard)"
      >
        <!-- 标准图标 -->
        <div class="standard-icon-wrapper" :style="{ background: standard.bgColor }">
          <i :class="standard.icon" :style="{ color: standard.iconColor }"></i>
        </div>

        <!-- 标准信息 -->
        <div class="standard-info">
          <div class="standard-header">
            <h4 class="standard-name">{{ standard.name }}</h4>
            <el-tag 
              v-if="standard.included" 
              size="mini" 
              type="success"
              effect="dark"
              class="included-tag"
            >
              {{ $t('buy.standards.included') }}
            </el-tag>
          </div>
          <p class="standard-fullname">{{ $t(`buy.standards.items.${standard.id}.fullname`) }}</p>
          <p class="standard-desc">{{ $t(`buy.standards.items.${standard.id}.desc`) }}</p>
          <div class="standard-meta">
            <el-tag size="mini" effect="plain" :type="getCategoryTagType(standard.category)">
              {{ $t(`buy.standards.categories.${standard.category}`) }}
            </el-tag>
            <!-- <span class="clause-count">
              <i class="el-icon-document"></i>
              {{ standard.clauseCount }} {{ $t('buy.standards.clauses') }}
            </span> -->
          </div>
        </div>

        <!-- 价格和选择 -->
        <div class="standard-action">
          <div class="standard-price" >
            <span class="price-amount">{{ currency }}  {{ formatPrice(standard.price) }}</span>
            <span class="price-period">/ {{ $t('buy.standards.perYear') }}</span>
          </div>
          <div class="standard-price free">
            <!-- <span class="price-amount">{{ $t('buy.standards.free') }}</span> -->
          </div>
          <div class="check-indicator" :class="{ checked: isSelected(standard.id) }">
            <i class="el-icon-check"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 提示信息 -->
    <div class="info-banner">
      <i class="el-icon-info"></i>
      <span>{{ $t('buy.standards.infoBanner') }}</span>
    </div>
  </div>
</template>

<script>
import { formatPrice as formatPriceValue } from '@/utils/currency'
export default {
  name: 'StandardSelect',
  props: {
    purchaseConfig: {
      type: Object,
      default: null
    },
    currency: {
      type: String,
      default: 'RMB'
    },
    selectedValue: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      activeCategory: 'all',
      selectedStandards: ['iso9001'],
      standardCatalog: [],
      standards: [
        {
          id: 'iso9001',
          name: 'ISO 9001:2015',
          icon: 'el-icon-medal',
          bgColor: 'rgba(59,130,246,0.1)',
          iconColor: '#3b82f6',
          category: 'quality',
          price: 1200,
          included: false,
          clauseCount: 62
        },
        {
          id: 'iatf16949',
          name: 'IATF 16949:2016',
          icon: 'el-icon-truck',
          bgColor: 'rgba(99,102,241,0.1)',
          iconColor: '#6366f1',
          category: 'quality',
          price: 1620,
          included: false,
          clauseCount: 85
        },
        {
          id: 'vda63',
          name: 'VDA 6.3:2023',
          icon: 'el-icon-s-operation',
          bgColor: 'rgba(236,72,153,0.1)',
          iconColor: '#ec4899',
          category: 'process',
          price: 1620,
          included: false,
          clauseCount: 48
        },
        {
          id: 'vda65',
          name: 'VDA 6.5',
          icon: 'el-icon-search',
          bgColor: 'rgba(245,158,11,0.1)',
          iconColor: '#f59e0b',
          category: 'product',
          price: 1200,
          included: false,
          clauseCount: 32
        },
        {
          id: 'iso14001',
          name: 'ISO 14001:2015',
          icon: 'el-icon-sunny',
          bgColor: 'rgba(16,185,129,0.1)',
          iconColor: '#10b981',
          category: 'environment',
          price: 1200,
          included: false,
          clauseCount: 51
        },

        {
          id: 'iso140012026',
          name: 'ISO 14001:2026',
          icon: 'el-icon-first-aid-kit',
          bgColor: 'rgba(239,68,68,0.1)',
          iconColor: '#ef4444',
          category: 'environment',
          price: 1200,
          included: false,
          clauseCount: 54
        },

        {
          id: 'tisax',
          name: 'TISAX',
          icon: 'el-icon-lock',
          bgColor: 'rgba(14,165,233,0.1)',
          iconColor: '#0ea5e9',
          category: 'security',
          price: 1620,
          included: false,
          clauseCount: 93
        },
        {
          id: 'iso13485',
          name: 'ISO 13485:2021',
          icon: 'el-icon-first-aid-kit',
          bgColor: 'rgba(239,68,68,0.1)',
          iconColor: '#ef4444',
          category: 'medical',
          price: 1620,
          included: false,
          clauseCount: 93
        },
             {
          id: 'iso45001',
          name: 'ISO 45001:2023',
          icon: 'el-icon-first-aid-kit',
          bgColor: 'rgba(239,68,68,0.1)',
          iconColor: '#ef4444',
          category: 'healthSafety',
          price: 1200,
          included: false,
          clauseCount: 54
        },
        {
          id: 'iso50001',
          name: 'ISO 50001:2018/Amd. 2024',
          icon: 'el-icon-sunny',
          bgColor: 'rgba(245,158,11,0.1)',
          iconColor: '#f59e0b',
          category: 'energy',
          price: 1200,
          included: false,
          clauseCount: 93
        },
                {
          id: 'iso27001',
          name: 'ISO 27001:2022',
          icon: 'el-icon-lock',
          bgColor: 'rgba(14,165,233,0.1)',
          iconColor: '#0ea5e9', 
          category: 'security',
          price: 1620,
          included: false,
          clauseCount: 93
        },
        {
          id: 'ISO-9001-2015',
          name: 'ISO 9001:2015',
          icon: 'el-icon-medal',
          bgColor: 'rgba(59,130,246,0.1)',
          iconColor: '#3b82f6',
          category: 'quality',
          price: 1200,
          included: false,
          clauseCount: 62
        },
        {
          id: 'ISO-14001-2026',
          name: 'ISO 14001:2026',
          icon: 'el-icon-sunny',
          bgColor: 'rgba(16,185,129,0.1)',
          iconColor: '#10b981',
          category: 'environment',
          price: 1200,
          included: false,
          clauseCount: 54
        },
        {
          id: 'ISO-13485-2016',
          name: 'ISO 13485:2016',
          icon: 'el-icon-first-aid-kit',
          bgColor: 'rgba(239,68,68,0.1)',
          iconColor: '#ef4444',
          category: 'medical',
          price: 1620,
          included: false,
          clauseCount: 93
        },
        {
          id: 'ISO-45001-2018',
          name: 'ISO 45001:2018',
          icon: 'el-icon-first-aid-kit',
          bgColor: 'rgba(239,68,68,0.1)',
          iconColor: '#ef4444',
          category: 'healthSafety',
          price: 1200,
          included: false,
          clauseCount: 54
        },
        {
          id: 'ISO-50001-2018',
          name: 'ISO 50001:2018',
          icon: 'el-icon-sunny',
          bgColor: 'rgba(245,158,11,0.1)',
          iconColor: '#f59e0b',
          category: 'energy',
          price: 1200,
          included: false,
          clauseCount: 93
        },
        {
          id: 'ISO-27001-2023',
          name: 'ISO/IEC 27001:2022',
          icon: 'el-icon-lock',
          bgColor: 'rgba(14,165,233,0.1)',
          iconColor: '#0ea5e9',
          category: 'security',
          price: 1620,
          included: false,
          clauseCount: 93
        },
        {
          id: 'ISO-14971-2019',
          name: 'ISO 14971:2019',
          icon: 'el-icon-warning-outline',
          bgColor: 'rgba(239,68,68,0.1)',
          iconColor: '#ef4444',
          category: 'medical',
          price: 1620,
          included: false,
          clauseCount: 45
        },
        {
          id: 'ISO-22163-2023',
          name: 'ISO 22163:2023',
          icon: 'el-icon-truck',
          bgColor: 'rgba(99,102,241,0.1)',
          iconColor: '#6366f1',
          category: 'quality',
          price: 1620,
          included: false,
          clauseCount: 78
        },
        {
          id: 'ISO-31000-2018',
          name: 'ISO 31000:2018',
          icon: 'el-icon-s-flag',
          bgColor: 'rgba(236,72,153,0.1)',
          iconColor: '#ec4899',
          category: 'process',
          price: 1200,
          included: false,
          clauseCount: 30
        },
        {
          id: 'customTemplate',
          name: 'Custom Template Creator',
          icon: 'el-icon-edit-outline',
          bgColor: 'rgba(139,92,246,0.1)',
          iconColor: '#8b5cf6',
          category: 'creator',
          price:  2400,
          included: false,
          clauseCount: 0
        }
      ]
    }
  },
  computed: {
    filteredStandards() {
      if (this.activeCategory === 'all') return this.standards
      return this.standards.filter(s => s.category === this.activeCategory)
    },
    moduleTotalPrice() {
      return this.selectedStandards.reduce((total, id) => {
        const std = this.standards.find(s => s.id === id)
        return total + (std ? std.price : 0)
      }, 0)
    }
  },
  methods: {
    emitSelection() {
      this.$emit('validate', this.selectedStandards.length > 0)
      this.$emit('standards-selected', {
        standards: [...this.selectedStandards],
        totalPrice: this.moduleTotalPrice
      })
    },
    applySelectedValue() {
      if (!Array.isArray(this.selectedValue)) return
      this.selectedStandards = [...this.selectedValue]
      this.syncSelectedStandardsWithAvailable()
    },
    applyPurchaseConfig() {
      if (!this.purchaseConfig || !Array.isArray(this.purchaseConfig.standards)) return
      const catalog = this.getStandardCatalog()
      this.standards = this.purchaseConfig.standards.map(item => {
        const standard = catalog.find(option => option.id === item.id) || this.createStandardFallback(item)
        return {
          ...standard,
          id: item.id,
          name: item.name || standard.name || item.id,
          price: item.price != null ? this.fromMinor(item.price) : standard.price
        }
      })
      this.syncSelectedStandardsWithAvailable()
      this.emitSelection()
    },
    getStandardCatalog() {
      const catalog = this.standardCatalog.length ? this.standardCatalog : this.standards
      return catalog.map(standard => ({ ...standard }))
    },
    createStandardFallback(item) {
      return {
        id: item.id,
        name: item.name || item.id,
        icon: 'el-icon-document',
        bgColor: 'rgba(100,116,139,0.1)',
        iconColor: '#64748b',
        category: 'quality',
        price: 0,
        included: false,
        clauseCount: 0
      }
    },
    syncSelectedStandardsWithAvailable() {
      if (!this.purchaseConfig || !Array.isArray(this.purchaseConfig.standards)) return
      const availableIds = this.standards.map(standard => standard.id)
      this.selectedStandards = this.selectedStandards.filter(id => availableIds.includes(id))
    },
    fromMinor(amount) {
      return Math.round(Number(amount || 0)) / 100
    },
    isSelected(id) {
      return this.selectedStandards.includes(id)
    },
    toggleStandard(standard) {
      if (standard.included) return // 已包含的不能取消
      
      if (this.isSelected(standard.id)) {
        this.selectedStandards = this.selectedStandards.filter(id => id !== standard.id)
      } else {
        this.selectedStandards.push(standard.id)
      }
      
      this.emitSelection()
    },
    getCategoryTagType(category) {
      const types = {
        quality: '',
        energy: 'warning',
        product: 'info',
        environment: 'success',
        process: 'warning',
        security: 'danger',
        medical: 'success',
        healthSafety: 'danger',
        creator: 'info'
      }
      return types[category] || ''
    },
    formatPrice(price) {
      if (price === 0) return formatPriceValue(0)
      if (!price) return formatPriceValue(0)
      return formatPriceValue(price)
    }
  },
  created() {
    this.standardCatalog = this.standards.map(standard => ({ ...standard }))
    this.applySelectedValue()
    this.applyPurchaseConfig()
    this.emitSelection()
  },
  watch: {
    purchaseConfig: {
      handler() {
        this.applyPurchaseConfig()
      },
      deep: true
    },
    selectedValue: {
      handler() {
        this.applySelectedValue()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.standard-select {
  margin: 0 auto;
  position: relative;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #6366f1, #818cf8);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 24px;
  i {
    font-size: 14px;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 30px;

  h3 {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
  }

  .section-desc {
    color: #64748b;
    font-size: 15px;
    margin: 0;
  }
}

.category-filter {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;

  ::v-deep .el-radio-button__inner {
    border-radius: 20px !important;
    padding: 8px 18px;
    border: none !important;
    font-size: 13px;
    transition: all 0.3s;
  }

  ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    background: linear-gradient(135deg, #6366f1, #818cf8);
    box-shadow: 0 4px 12px rgba(99,102,241,0.3);
  }
}

.selection-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #f0f4ff, #ede9fe);
  border-radius: 12px;
  margin-bottom: 24px;

  .summary-left {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4f46e5;
    font-weight: 600;
    font-size: 14px;

    i {
      font-size: 18px;
    }
  }

  .summary-right {
    .total-label {
      color: #64748b;
      font-size: 14px;
    }
    .total-amount {
      font-size: 22px;
      font-weight: 700;
      color: #1e293b;
      margin: 0 4px;
    }
    .total-period {
      color: #94a3b8;
      font-size: 13px;
    }
  }
}

.standards-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.standard-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fff;

  &:hover {
    border-color: #c7d2fe;
    box-shadow: 0 4px 16px rgba(99,102,241,0.08);
    transform: translateX(4px);
  }

  &.selected {
    border-color: #6366f1;
    background: linear-gradient(135deg, #faf5ff 0%, #fff 100%);
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
  }

  &.included {
    border-color: #86efac;
    background: linear-gradient(135deg, #f0fdf4 0%, #fff 100%);
    cursor: default;
  }
}

.standard-icon-wrapper {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 24px;
  }
}

.standard-info {
  flex: 1;
  min-width: 0;

  .standard-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 4px;
  }

  .standard-name {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
  }

  .included-tag {
    border-radius: 12px;
  }

  .standard-fullname {
    font-size: 13px;
    color: #64748b;
    margin: 0 0 6px;
  }

  .standard-desc {
    font-size: 13px;
    color: #94a3b8;
    margin: 0 0 8px;
    line-height: 1.4;
  }

  .standard-meta {
    display: flex;
    align-items: center;
    gap: 12px;

    .clause-count {
      font-size: 12px;
      color: #94a3b8;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
}

.standard-action {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex-shrink: 0;

  .standard-price {
    text-align: right;

    .price-amount {
      font-size: 20px;
      font-weight: 700;
      color: #1e293b;
    }

    .price-period {
      font-size: 13px;
      color: #94a3b8;
    }

    &.free .price-amount {
      color: #10b981;
      font-size: 16px;
    }
  }

  .check-indicator {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    i {
      font-size: 14px;
      color: transparent;
      transition: all 0.3s;
    }

    &.checked {
      background: #6366f1;
      border-color: #6366f1;

      i {
        color: white;
      }
    }
  }
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  margin-top: 20px;
  font-size: 13px;
  color: #92400e;

  i {
    font-size: 18px;
    color: #f59e0b;
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .step-badge {
    padding: 5px 12px;
    margin-bottom: 16px;
    font-size: 12px;
  }

  .section-header {
    text-align: left;
    margin-bottom: 22px;

    h3 {
      font-size: 21px;
    }

    .section-desc {
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .category-filter {
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch;

    ::v-deep .el-radio-group {
      display: flex;
      flex-wrap: nowrap;
      gap: 8px;
      min-width: max-content;
    }
  }

  .selection-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
  }

  .standard-card {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
    padding: 14px;
    border-width: 1px;
    border-radius: 12px;

    &:hover {
      transform: none;
    }
  }

  .standard-info {
    width: 100%;
  }

  .standard-action {
    width: 100%;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;

    .standard-price {
      text-align: left;

      .price-amount {
        font-size: 17px;
      }
    }
  }

  .info-banner {
    align-items: flex-start;
    padding: 12px 14px;
  }
}

@media (max-width: 480px) {
  .standard-icon-wrapper {
    width: 44px;
    height: 44px;
  }

  .standard-info .standard-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .selection-summary .summary-right .total-amount {
    display: inline-block;
    font-size: 18px;
  }
}
</style>
